/**
 * Extracts a meaningful, specific error message from an API error response.
 * Handles Axios errors, string errors, response payloads with message, msg, error,
 * or validation errors array/object.
 *
 * @param {any} error - The error object caught from a try/catch or API call
 * @param {string} fallback - Default fallback message if no specific backend message is found
 * @returns {string} The formatted error message
 */
export const getApiErrorMessage = (error, fallback = "An error occurred. Please try again.") => {
  if (!error) {
    return fallback;
  }

  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }

  // If error is an Axios error, the backend response payload is in error.response.data
  const responseData = error?.response?.data || error?.data || error;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }

  // 1. Direct message / msg / error strings
  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message.trim();
  }

  if (typeof responseData?.msg === "string" && responseData.msg.trim()) {
    return responseData.msg.trim();
  }

  if (typeof responseData?.error === "string" && responseData.error.trim()) {
    return responseData.error.trim();
  }

  if (typeof responseData?.error?.message === "string" && responseData.error.message.trim()) {
    return responseData.error.message.trim();
  }

  // 2. Validation errors array (e.g. Express-validator, backend validation lists)
  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    const errorStrings = responseData.errors
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.msg || item?.message || item?.error;
      })
      .filter(Boolean);
    if (errorStrings.length > 0) {
      return errorStrings.join(", ");
    }
  }

  // 3. Validation errors object (e.g. { field1: 'msg1', field2: 'msg2' })
  if (responseData?.errors && typeof responseData.errors === "object") {
    const values = Object.values(responseData.errors)
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.msg || item?.message || item?.error;
      })
      .filter(Boolean);
    if (values.length > 0) {
      return values.join(", ");
    }
  }

  // 4. Raw error.message (ignore generic Axios status code errors unless no other info)
  if (
    typeof error?.message === "string" &&
    error.message.trim() &&
    !error.message.startsWith("Request failed with status code")
  ) {
    return error.message.trim();
  }

  return fallback;
};
